import { useUser } from "$global-providers/user-provider/provider";
import { randomUniqueId } from "$routes/_lib/random-id";
import type { EpocusEncounter } from "../../../global-providers/rxdb-provider/collections/epocus-encounter";
import { useRxCollectionEpocusEncounter } from "../../../global-providers/rxdb-provider/collections/epocus-encounter";
import type { EpocusExam } from "../../../global-providers/rxdb-provider/collections/epocus-exam";
import { useRxCollectionEpocusExam } from "../../../global-providers/rxdb-provider/collections/epocus-exam";
import {
  EXAM_TYPES,
  type ExamType,
  NORMAL_WORKSHEET_BY_TYPE,
} from "../../../global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import type { EpocusReview } from "../../../global-providers/rxdb-provider/collections/epocus-review";
import { useRxCollectionEpocusReview } from "../../../global-providers/rxdb-provider/collections/epocus-review";

const MOCK_PERFORMER_ID = "mock-performer-1";

const mockMeta = (date: string) => ({
  createdAt: date,
  createdByUserId: MOCK_PERFORMER_ID,
  updatedAt: date,
  updatedByUserId: MOCK_PERFORMER_ID,
});

const EXAM_TEMPLATES = [
  (encounterId: string, date: string): EpocusExam => ({
    id: randomUniqueId(),
    encounterId,
    signedAt: null,
    signedByUserId: null,
    nonBillable: false,
    nonBillableReason: null,
    amendments: [],
    examType: "cardiac",
    status: "submitted",
    worksheetData: {
      examType: "cardiac",
      indication: "dyspnea",
      views: { plax: true, psax: true, apical4Chamber: true, subxiphoid: true },
      findings: {
        lvFunction: "normal",
        rvDilatation: false,
        pericardialEffusion: "none",
        ivcCollapsible: true,
        ivcDiameterCm: 1.8,
        spectralDoppler: false,
        colorFlowDoppler: false,
      },
      notes: "Normal study.",
    },
    images: [
      {
        id: randomUniqueId(),
        previewS3Key: "mock/cardiac/plax-001.jpg",
        viewLabel: "PLAX",
        dicomPreset: null,
      },
      {
        id: randomUniqueId(),
        previewS3Key: "mock/cardiac/psax-001.jpg",
        viewLabel: "PSAX",
        dicomPreset: null,
      },
      {
        id: randomUniqueId(),
        previewS3Key: "mock/cardiac/apical4-001.jpg",
        viewLabel: "Apical 4Ch",
        dicomPreset: null,
      },
    ],
    meta: mockMeta(date),
  }),
  (encounterId: string, date: string): EpocusExam => ({
    id: randomUniqueId(),
    encounterId,
    signedAt: null,
    signedByUserId: null,
    nonBillable: false,
    nonBillableReason: null,
    amendments: [],
    examType: "efast",
    status: "submitted",
    worksheetData: {
      examType: "efast",
      indication: "blunt_trauma",
      views: {
        ruq: true,
        luq: true,
        suprapubic: true,
        subxiphoid: true,
        rLung: true,
        lLung: true,
      },
      findings: {
        ruqFreeFluid: "absent",
        luqFreeFluid: "absent",
        pelvicFreeFluid: "absent",
        pericardialEffusion: "absent",
        rPneumothorax: "absent",
        lPneumothorax: "absent",
      },
      notes: null,
    },
    images: [
      {
        id: randomUniqueId(),
        previewS3Key: "mock/efast/ruq-001.jpg",
        viewLabel: "RUQ",
        dicomPreset: null,
      },
      {
        id: randomUniqueId(),
        previewS3Key: "mock/efast/luq-001.jpg",
        viewLabel: "LUQ",
        dicomPreset: null,
      },
      {
        id: randomUniqueId(),
        previewS3Key: "mock/efast/subxiphoid-001.jpg",
        viewLabel: "Subxiphoid",
        dicomPreset: null,
      },
    ],
    meta: mockMeta(date),
  }),
  (encounterId: string, date: string): EpocusExam => ({
    id: randomUniqueId(),
    encounterId,
    signedAt: null,
    signedByUserId: null,
    nonBillable: false,
    nonBillableReason: null,
    amendments: [],
    examType: "lung",
    status: "submitted",
    worksheetData: {
      examType: "lung",
      indication: "dyspnea",
      views: {
        rUpperAnterior: true,
        rLowerAnterior: true,
        rPosterior: true,
        lUpperAnterior: true,
        lLowerAnterior: true,
        lPosterior: true,
      },
      zones: {
        rUpper: {
          lungSliding: true,
          bLines: "0",
          pleuralEffusion: "none",
          consolidation: false,
        },
        rLower: {
          lungSliding: true,
          bLines: "1-2",
          pleuralEffusion: "none",
          consolidation: false,
        },
        lUpper: {
          lungSliding: true,
          bLines: "0",
          pleuralEffusion: "none",
          consolidation: false,
        },
        lLower: {
          lungSliding: true,
          bLines: "0",
          pleuralEffusion: "none",
          consolidation: false,
        },
      },
      notes: null,
    },
    images: [
      {
        id: randomUniqueId(),
        previewS3Key: "mock/lung/r-upper-001.jpg",
        viewLabel: "R Upper",
        dicomPreset: null,
      },
      {
        id: randomUniqueId(),
        previewS3Key: "mock/lung/l-upper-001.jpg",
        viewLabel: "L Upper",
        dicomPreset: null,
      },
    ],
    meta: mockMeta(date),
  }),
];

const PATIENT_NAMES = [
  "Maria Garcia",
  "James Wilson",
  "Linda Park",
  "Aisha Okonkwo",
  "Samuel Adeyemi",
];

// ─── Bulk seed data ───────────────────────────────────────────────────────────

const FEEDBACK_TAGS = [
  "Great Job",
  "Good Technique",
  "Good Image Quality",
  "Too Shallow",
  "Too Dark",
  "Probe Placement",
  "Needs Improvement",
  "Complete Documentation",
];

const BULK_SCORES = [
  3.5, 4.0, 4.5, 5.0, 3.0, 4.0, 4.5, 4.0, 3.5, 4.5, 5.0, 4.0, 3.5, 4.0, 4.5,
  4.0, 4.5, 3.5, 5.0, 4.0, 4.5, 4.0, 3.0, 4.5, 4.0, 4.5, 5.0, 3.5, 4.0, 4.5,
];

const makeBulkExam = (
  encounterId: string,
  examType: ExamType,
  date: string,
): EpocusExam => ({
  id: randomUniqueId(),
  encounterId,
  signedAt: null,
  signedByUserId: null,
  nonBillable: false,
  nonBillableReason: null,
  amendments: [],
  examType,
  status: "submitted",
  worksheetData: NORMAL_WORKSHEET_BY_TYPE[examType],
  images: [
    {
      id: randomUniqueId(),
      previewS3Key: `mock/${examType}/view-001.jpg`,
      viewLabel: "View 1",
      dicomPreset: null,
    },
  ],
  meta: mockMeta(date),
});

const makeBulkReview = (
  examId: string,
  examType: ExamType,
  score: number,
  date: string,
  tagIndices: number[],
  reviewerId: string,
): EpocusReview => ({
  id: randomUniqueId(),
  examId,
  reviewerId,
  agreement: score >= 3.5 ? "agree" : "disagree",
  qualityScore: score,
  feedbackTags: tagIndices.map((i) => FEEDBACK_TAGS[i % FEEDBACK_TAGS.length]),
  comment: score < 3.5 ? "Needs improvement on image quality." : null,
  originalFindings: NORMAL_WORKSHEET_BY_TYPE[examType],
  revisedFindings: null,
  meta: {
    createdAt: date,
    createdByUserId: reviewerId,
    updatedAt: date,
    updatedByUserId: reviewerId,
  },
});

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useEpocusSeedData = () => {
  const user = useUser();
  const encounterCollection = useRxCollectionEpocusEncounter();
  const examCollection = useRxCollectionEpocusExam();
  const reviewCollection = useRxCollectionEpocusReview();

  const seed = async () => {
    if (!encounterCollection || !examCollection) return;

    const encounterId = randomUniqueId();
    const date = new Date().toISOString();
    const patientName =
      PATIENT_NAMES[Math.floor(Math.random() * PATIENT_NAMES.length)];
    const examTemplate =
      EXAM_TEMPLATES[Math.floor(Math.random() * EXAM_TEMPLATES.length)];

    const encounter: EpocusEncounter = {
      id: encounterId,
      status: "pending_review",
      performerId: MOCK_PERFORMER_ID,
      deviceId: "ge-vscan-air",
      location: null,
      elephantPatientId: null,
      patientName,
      accessionNumber: null,
      date,
      meta: mockMeta(date),
    };

    await encounterCollection.insert(encounter);
    await examCollection.insert(examTemplate(encounterId, date));
  };

  // Inserts 30 encounters, one exam per encounter (cycling through all 6 exam
  // types), and a review for each exam. Spread over 90 days so trend charts
  // show meaningful data.
  const bulkSeed = async () => {
    if (!encounterCollection || !examCollection || !reviewCollection) return;

    const now = Date.now();
    const DAY_MS = 24 * 60 * 60 * 1000;
    const TOTAL = 30;

    for (let i = 0; i < TOTAL; i++) {
      const examType = EXAM_TYPES[i % EXAM_TYPES.length];
      const daysAgo = Math.round(((TOTAL - 1 - i) / (TOTAL - 1)) * 89);
      const date = new Date(now - daysAgo * DAY_MS).toISOString();
      const patientName = PATIENT_NAMES[i % PATIENT_NAMES.length];
      const encounterId = randomUniqueId();

      const encounter: EpocusEncounter = {
        id: encounterId,
        status: "complete",
        performerId: user.id,
        deviceId: "ge-vscan-air",
        location: null,
        elephantPatientId: null,
        patientName,
        accessionNumber: `ACC-${1000 + i}`,
        date,
        meta: mockMeta(date),
      };

      const exam = makeBulkExam(encounterId, examType, date);

      // Vary feedback: mostly "Great Job" but sprinkle in technique feedback
      const primaryTag = i % 5 === 0 ? 3 : 0; // index 3 = "Too Shallow", 0 = "Great Job"
      const secondaryTag = i % 3 === 0 ? 1 : 7; // "Good Technique" or "Complete Documentation"
      const score = BULK_SCORES[i % BULK_SCORES.length];
      const review = makeBulkReview(
        exam.id,
        examType,
        score,
        date,
        [primaryTag, secondaryTag],
        user.id,
      );

      await Promise.all([
        encounterCollection.insert(encounter),
        examCollection.insert(exam),
        reviewCollection.insert(review),
      ]);
    }
  };

  return { seed, bulkSeed };
};
